import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import Head from 'next/head'
import { useRouter } from 'next/router'

import useSWR from 'swr'

import { getBaseUrl, serializeCacheKey } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { replaceHtmlEntities } from '@/utils/string/replaceHtmlEntities'
import { useUrl } from '@/utils/url/useUrl'

import { getShowcaseCategories } from '../helpers/getShowcaseCategories'

type Props = {
	children: React.ReactNode
}

export function ShowcaseMeta({ children }: Props) {
	const { query, locale = 'en-US' } = useRouter()
	const slug = typeof query?.slug === 'string' ? query?.slug : query.slug?.at(-1)

	const { data: categories } = useSWR(
		serializeCacheKey('showcaseCategory', { limit: 100, locale }),
		async () => await getShowcaseCategories(0, 100, locale)
	)

	const showcase = categories?.find((category) => category?.slug_en_US === 'showcase')
	const category =
		categories?.find((category) => category?.slug === slug) ??
		categories?.find((category) => category?.slug_en_US === 'showcase')
	const categoryParent = category?.parentCategory

	const getSlugPath = (locale: string) => {
		if (showcase?.slug === category?.slug) {
			return ''
		}

		const slugPath = category?.[`slug_${locale}` as 'slug_en_US']
		const parentSlugPath = categoryParent?.[`slug_${locale}` as 'slug_en_US']

		if (parentSlugPath) {
			return `${parentSlugPath}/${slugPath}/`
		}

		if (slugPath) {
			return `${slugPath}/`
		}
		return ''
	}

	const url = useUrl({ locale })
	const baseUrl = `${url?.protocol}//${url?.host}`
	const { seoMetadata } = category ?? {}
	const includeTitleTemplate = Boolean(seoMetadata?.includeTitleTemplate)

	const title = includeTitleTemplate
		? `${seoMetadata?.title} | BigCommerce`
		: seoMetadata?.title ?? 'Featured Ecommerce Platform | BigCommerce'

	const description = seoMetadata?.description
		? replaceHtmlEntities(`${seoMetadata?.description}`)
		: 'Learn how BigCommerce can fuel your business with all the capabilities of enterprise ecommerce—without the cost or complexity.'

	return (
		<>
			<Head>
				<meta name="og:locale:alternate" content="en_US" key="en_US" />
				<meta name="og:locale:alternate" content="en_GB" key="en_GB" />
				<meta name="og:locale:alternate" content="en_AU" key="en_AU" />
				<meta name="og:locale:alternate" content="en_SG" key="en_SG" />
				<meta name="og:locale:alternate" content="fr_FR" key="fr_FR" />
				<meta name="og:locale:alternate" content="it_IT" key="it_IT" />
				<meta name="og:locale:alternate" content="nl_NL" key="nl_NL" />
				<meta name="og:locale:alternate" content="es_ES" key="es_ES" />
				<meta name="og:locale:alternate" content="es_MX" key="es_MX" />
				<meta name="og:locale:alternate" content="de_DE" key="de_DE" />
				<meta name="og:locale:alternate" content="de_AT" key="de_AT" />
				<meta name="og:locale:alternate" content="sv_SE" key="sv_SE" />
				<meta name="og:locale:alternate" content="da_DK" key="da_DK" />
				<meta name="og:locale:alternate" content="no_NO" key="no-NO" />
			</Head>
			<NextSeo
				title={title}
				description={description}
				canonical={
					seoMetadata?.canonicalUrl ||
					`${baseUrl}/${showcase?.slug}/${getSlugPath(locale.replace('-', '_'))}`
				}
				languageAlternates={[
					{
						hrefLang: 'en-us',
						href: `${getBaseUrl('en-US')}/${showcase?.slug_en_US}/${getSlugPath('en_US')}`
					},
					{
						hrefLang: 'en-au',
						href: `${getBaseUrl('en-AU')}/${showcase?.slug_en_AU}/${getSlugPath('en_AU')}`
					},
					{
						hrefLang: 'en-gb',
						href: `${getBaseUrl('en-GB')}/${showcase?.slug_en_GB}/${getSlugPath('en_GB')}`
					},
					{
						hrefLang: 'en-sg',
						href: `${getBaseUrl('en-SG')}/${showcase?.slug_en_SG}/${getSlugPath('en_SG')}`
					},
					{
						hrefLang: 'fr-fr',
						href: `${getBaseUrl('fr-FR')}/${showcase?.slug_fr_FR}/${getSlugPath('fr_FR')}`
					},
					{
						hrefLang: 'it-it',
						href: `${getBaseUrl('it-IT')}/${showcase?.slug_it_IT}/${getSlugPath('it_IT')}`
					},
					{
						hrefLang: 'sv-se',
						href: `${getBaseUrl('sv-SE')}/${showcase?.slug_sv_SE}/${getSlugPath('sv_SE')}`
					},
					{
						hrefLang: 'da-dk',
						href: `${getBaseUrl('da-DK')}/${showcase?.slug_da_DK}/${getSlugPath('da_DK')}`
					},
					{
						hrefLang: 'no-no',
						href: `${getBaseUrl('nb-NO')}/${showcase?.slug_nb_NO}/${getSlugPath('nb_NO')}`
					},
					{
						hrefLang: 'de-at',
						href: `${getBaseUrl('de-AT')}/${showcase?.slug_de_AT}/${getSlugPath('de_AT')}`
					},
					{
						hrefLang: 'de-de',
						href: `${getBaseUrl('de-DE')}/${showcase?.slug_de_DE}/${getSlugPath('de_DE')}`
					},
					{
						hrefLang: 'nl-nl',
						href: `${getBaseUrl('nl-NL')}/${showcase?.slug_nl_NL}/${getSlugPath('nl_NL')}`
					},
					{
						hrefLang: 'es-es',
						href: `${getBaseUrl('es-ES')}/${showcase?.slug_es_ES}/${getSlugPath('es_ES')}`
					},
					{
						hrefLang: 'es-mx',
						href: `${getBaseUrl('es-MX')}/${showcase?.slug_es_MX}/${getSlugPath('es_MX')}`
					},
					{
						hrefLang: 'x-default',
						href: `${getBaseUrl()}/${showcase?.slug_en_US}/${getSlugPath('en_US')}`
					}
				]}
				nofollow={Boolean(seoMetadata?.nofollow)}
				noindex={Boolean(seoMetadata?.noindex)}
				openGraph={{
					title,
					description,
					url: `${baseUrl}/${showcase?.slug}/${getSlugPath(locale.replace('-', '_'))}`,
					images: seoMetadata?.image?.url
						? [
								{
									url: seoMetadata?.image?.url,
									width: seoMetadata?.image?.width,
									height: seoMetadata?.image?.height,
									alt: 'Bigcommerce logo',
									type: 'image/jpeg'
								}
						  ]
						: undefined,
					locale: getSeoLocale(locale),
					siteName: 'BigCommerce'
				}}
			/>
			<BreadcrumbJsonLd
				itemListElements={[
					{
						position: 1,
						name: 'Homepage',
						item: `${getBaseUrl(locale)}/`
					},
					{
						position: 2,
						name: showcase?.name,
						item: `${getBaseUrl(locale)}/${showcase?.slug}/`
					},
					categoryParent?.slug
						? {
								position: 3,
								name: categoryParent?.name,
								item: `${getBaseUrl(locale)}/${showcase?.slug}/${categoryParent.slug}/`
						  }
						: null,
					category?.slug && category.slug !== showcase?.slug
						? {
								position: categoryParent?.slug ? 4 : 3,
								name: category?.name,
								item: `${getBaseUrl(locale)}/${showcase?.slug}/${
									categoryParent?.slug ? `${categoryParent.slug}/` : ''
								}${category.slug}/`
						  }
						: null
				].filter(Boolean)}
			/>

			{children}
		</>
	)
}
